/**
 * App Version — bump this string every time you deploy a new version.
 * 
 * NON-DESTRUCTIVE: We no longer clear localStorage/caches/SW on version change.
 * The Service Worker (with skipWaiting + clientsClaim) handles the update automatically.
 * 
 * Emergency crash recovery is handled by the inline script in index.html,
 * which detects reload loops and auto-heals BEFORE any bundled JS runs.
 */
export const APP_VERSION = "82.71.0";

const STORAGE_KEY = "app_version";

export function checkAppVersion(): void {
  if (typeof window === "undefined") return;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored && stored !== APP_VERSION) {
      console.log(`[AppVersion] New version detected: ${APP_VERSION} (was ${stored}).`);
      // Just update the stored version. The SW handles precache updates automatically.
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
    } else if (!stored) {
      // First visit
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
    }

    // Request persistent storage (non-blocking)
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().catch(() => { });
    }
  } catch {
    // Storage blocked — ignore
  }
}

/**
 * Register a global error handler for chunk/module loading failures.
 * Instead of auto-reloading (which can cause loops that crash tablets),
 * we log the error. The inline script in index.html handles crash recovery.
 */
export function registerChunkErrorRecovery(): void {
  if (typeof window === "undefined") return;

  // Handle dynamic import() failures (lazy routes)
  window.addEventListener("error", (event) => {
    const msg = event.message || "";
    if (
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("Importing a module script failed")
    ) {
      console.warn("[ChunkRecovery] Stale chunk detected:", msg);
      // Don't auto-reload here — the index.html inline script
      // handles loop detection and recovery safely.
    }
  });

  // Handle unhandled promise rejections (another form of chunk load failure)
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason?.message || String(event.reason || "");
    if (
      reason.includes("Failed to fetch dynamically imported module") ||
      reason.includes("Loading chunk") ||
      reason.includes("Importing a module script failed")
    ) {
      console.warn("[ChunkRecovery] Stale chunk (promise):", reason);
    }
  });
}
